/**
 * Coloring bookmarks — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const coloringBookmarksPack: ToilePack = {
    slug: 'coloring-bookmarks',
    category: 'coloring',
    name: 'Coloring bookmarks',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bookish', label: 'Bookish', subject: 'a stack of books, an open book with a ribbon marker and a sleeping cat'},
        {key: 'woodland', label: 'Woodland', subject: 'a fox among wildflowers, a cluster of mushrooms, a fern frond and a hedgehog'},
        {key: 'space', label: 'Space', subject: 'a rocket trailing stars, a ringed planet, a crescent moon and a tiny astronaut'},
    ],
    styles: [
        {key: 'bold', label: 'Bold', style: 'in one thick uniform black outline with large simple shapes and wide open areas to color', palette: 'black ink on white paper only', framing: 'Flat 2D line art as printed in a coloring book, the four strips centered with a white margin'},
        {key: 'fine', label: 'Fine', style: 'in a fine delicate black line with small ornamental detail packed down each strip', palette: 'black ink on white paper only', framing: 'A flat design sheet seen straight on, the strips isolated on clean white paper with a margin'},
        {key: 'mandala', label: 'Mandala', style: 'with the motifs dissolved into symmetrical banded mandala patterns running down each strip in an even thin line', palette: 'black ink on white paper only', framing: 'Flat 2D line art on a plain white paper sheet, the four strips centered with a white margin'},
    ],
    directions: ['dinosaurs instead', 'a Halloween set instead', 'for a teacher gift'],
    seo: {
        title: 'Coloring bookmarks: bookish, woodland, space',
        metaDescription: 'Coloring bookmarks — bookish, woodland, space — four to a sheet with cut lines, bold, fine or mandala style, a hole for a tassel.',
        h1: 'Coloring bookmarks: bookish, woodland and space in three styles',
        tagline: 'Four bookmarks a sheet, three themes, three styles each — a hole marked at the top for a tassel.',
        intro: 'Four tall bookmarks to a sheet with cut lines, each a narrow coloring page: a stack of books and a sleeping cat, a fox among wildflowers, a rocket trailing stars. Bold and simple, fine and ornamental, or dissolved into a banded mandala pattern. A hole is marked at the top of each for a tassel or string.',
        useCases: [
            'a library craft table',
            'a classroom reward for finishing a book',
            'a reading-club activity',
            'a handmade gift for a reader',
        ],
        faq: [
            {q: 'What size do the bookmarks print at?', a: 'The sheet is drawn at portrait A4 proportions (832 by 1216) with four bookmarks and cut lines between them.'},
            {q: 'How many bookmarks are on one sheet?', a: 'Four, each with a hole mark at the top for a tassel or string once cut apart.'},
            {q: 'Can I get a different bookmark theme?', a: 'Yes — that is what the direction field on the page is for. Type a different theme and the sheet is drawn again in the same three styles.'},
        ],
    },
}
